import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/http';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/dashboard/prontuarios/$clientId')({ component: ProntuarioDetail });

const MODALITY_LABEL: Record<string, string> = {
  ONLINE: 'Online',
  PRESENCIAL: 'Presencial',
  HYBRID: 'Online ou Presencial',
};

const GENDER_LABEL: Record<string, string> = {
  MALE: 'Masculino', male: 'Masculino',
  FEMALE: 'Feminino', female: 'Feminino',
  NON_BINARY: 'Não-binário', 'non-binary': 'Não-binário',
  OTHER: 'Outro', other: 'Outro',
};

const money = (v: any) =>
  v == null ? '' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function calcAge(birthIso?: string | null): number | null {
  if (!birthIso) return null;
  const d = new Date(birthIso);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age;
}

function formatPhone(digits: string): string {
  const d = (digits ?? '').replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  if (d.length === 10) return d.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  return digits;
}

function ProntuarioDetail() {
  const { clientId } = Route.useParams();
  const { data, isLoading } = useQuery({
    queryKey: ['notes-by-client', clientId],
    queryFn: () => api.get(`sessions/notes/by-client/${clientId}`).json<{ client: any; sessions: any[] }>(),
  });

  const sessions = data?.sessions ?? [];
  const client = data?.client;
  const done = sessions.filter((s) => s.status === 'DONE');
  const withNote = done.filter((s) => s.sessionNote);
  const cancelled = sessions.filter((s) => s.status === 'CANCELLED');
  const firstDone = [...done].reverse()[0];  // done vem DESC → last é o primeiro cronológico
  const lastDone = done[0];

  return (
    <div className="max-w-3xl space-y-6">
      <Link to="/dashboard/clientes" className="text-sm text-teal-600 hover:underline inline-block">
        ← Voltar
      </Link>

      {isLoading ? (
        <Card>
          <CardContent className="p-8 animate-pulse h-32" />
        </Card>
      ) : !client ? (
        <Card><CardContent className="p-12 text-center">
          <p className="text-slate-500 text-sm">Cliente não encontrado.</p>
        </CardContent></Card>
      ) : (
        <>
          {/* Card do cliente */}
          <ClientCard
            client={client}
            summary={{ done: done.length, withNote: withNote.length, cancelled: cancelled.length, firstDone, lastDone }}
          />

          {/* Timeline */}
          <div>
            <h2 className="text-lg font-bold text-slate-800 mb-3">Sessões</h2>
            {sessions.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-slate-500 text-sm">
                    Nenhuma sessão realizada com este cliente ainda.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {sessions.map((s) => <SessionCard key={s.id} session={s} />)}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ClientCard({
  client,
  summary,
}: {
  client: any;
  summary: { done: number; withNote: number; cancelled: number; firstDone?: any; lastDone?: any };
}) {
  const name = client.user?.name ?? 'Sem nome';
  const initial = name.charAt(0).toUpperCase();
  const avatar =
    (client.avatarUrl && !client.avatarUrl.startsWith('data:') ? client.avatarUrl : null)
    ?? client.user?.image;
  const age = calcAge(client.birthDate);
  const city = [client.city, client.state].filter(Boolean).join(' / ');

  const dateStr = (iso?: string) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start gap-4 flex-wrap">
          {avatar ? (
            <img src={avatar} alt="" className="size-20 rounded-2xl object-cover ring-4 ring-teal-50 shrink-0" />
          ) : (
            <div className="size-20 rounded-2xl bg-gradient-to-br from-teal-300 to-coral-300 flex items-center justify-center text-white text-3xl font-bold shrink-0">
              {initial}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-extrabold text-slate-900 leading-tight">{name}</h1>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-sm text-slate-500">
              {client.user?.email && <span>{client.user.email}</span>}
              {client.phone && (
                <>
                  <span>·</span>
                  <span>
                    {formatPhone(client.phone)}
                    {client.whatsappOptIn && <span className="ml-1 text-green-600" title="Recebe WhatsApp">📱</span>}
                  </span>
                </>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-sm text-slate-500">
              {age != null && <span>{age} anos</span>}
              {client.gender && <span>· {GENDER_LABEL[client.gender] ?? client.gender}</span>}
              {city && <span>· {city}</span>}
              {client.preferredModality && <span>· prefere {MODALITY_LABEL[client.preferredModality] ?? client.preferredModality}</span>}
            </div>
          </div>
        </div>

        {/* Métricas */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Metric label="Sessões" value={String(summary.done)} />
          <Metric label="Com evolução" value={`${summary.withNote}/${summary.done}`} />
          <Metric label="Primeira" value={dateStr(summary.firstDone?.scheduledAt)} />
          <Metric label="Última" value={dateStr(summary.lastDone?.scheduledAt)} />
        </div>

        {client.notes && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Observações do cliente</p>
            <p className="text-sm text-slate-600 whitespace-pre-line">{client.notes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-50 rounded-lg px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
      <p className="text-lg font-bold text-slate-800 mt-0.5 capitalize">{value}</p>
    </div>
  );
}

function SessionCard({ session }: { session: any }) {
  const isCancelled = session.status === 'CANCELLED';
  const [expanded, setExpanded] = useState(false);
  const date = new Date(session.scheduledAt);
  const dateFmt = date.toLocaleDateString('pt-BR', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
  const timeFmt = date.toLocaleTimeString('pt-BR', {
    hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
  });
  const hasNote = !!session.sessionNote;

  return (
    <Card className={cn(isCancelled && 'opacity-70')}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-bold text-slate-800 capitalize">{dateFmt}</p>
              {isCancelled ? (
                <span className="text-xs bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded">Cancelada</span>
              ) : hasNote ? (
                <span className="text-xs bg-teal-50 text-teal-700 border border-teal-200 px-2 py-0.5 rounded">Documentada</span>
              ) : (
                <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded">Sem evolução</span>
              )}
            </div>
            <div className="text-sm text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
              <span>{timeFmt}</span>
              <span>·</span>
              <span>
                {session.meetActualDurationMin ?? session.durationMin ?? 50} min
                {session.meetActualDurationMin && session.meetActualDurationMin !== session.durationMin && (
                  <span className="text-slate-400"> (contratado: {session.durationMin} min)</span>
                )}
              </span>
              <span>·</span>
              <span>{MODALITY_LABEL[session.modality] ?? session.modality}</span>
              {session.priceCharged != null && Number(session.priceCharged) > 0 && (
                <>
                  <span>·</span>
                  <span>{money(session.priceCharged)}</span>
                </>
              )}
            </div>
          </div>
          {!isCancelled && (
            <Link
              to="/dashboard/sessoes/$id"
              params={{ id: session.id }}
              className="text-sm text-teal-600 hover:text-teal-700 font-semibold hover:underline shrink-0"
            >
              {hasNote ? 'Editar evolução →' : 'Registrar evolução →'}
            </Link>
          )}
        </div>

        {isCancelled && session.cancellationReason && (
          <p className="mt-3 text-xs text-slate-500 bg-slate-50 rounded p-2">
            <strong>Motivo:</strong> {session.cancellationReason}
            {session.cancelledBy && <> · <em>por {session.cancelledBy === 'THERAPIST' ? 'profissional' : 'cliente'}</em></>}
          </p>
        )}

        {hasNote && (
          <div className="mt-4 border-t border-slate-100 pt-3">
            {expanded ? (
              <div className="space-y-2 text-sm text-slate-700">
                {session.sessionNote.chiefComplaint && (
                  <p><strong className="text-slate-800">Queixa:</strong> {session.sessionNote.chiefComplaint}</p>
                )}
                <p><strong className="text-slate-800">Evolução:</strong> <span className="whitespace-pre-line">{session.sessionNote.evolution}</span></p>
                {session.sessionNote.plan && (
                  <p><strong className="text-slate-800">Plano:</strong> <span className="whitespace-pre-line">{session.sessionNote.plan}</span></p>
                )}
                <button
                  onClick={() => setExpanded(false)}
                  className="text-xs text-slate-500 hover:text-slate-700 mt-2"
                >
                  Recolher
                </button>
              </div>
            ) : (
              <button
                onClick={() => setExpanded(true)}
                className="text-sm text-slate-600 hover:text-slate-800 leading-relaxed text-left w-full"
              >
                <span className="text-slate-700 line-clamp-2 whitespace-pre-line">
                  {session.sessionNote.evolution}
                </span>
                <span className="text-xs text-teal-600 mt-1 inline-block">Ver completo ↓</span>
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
